"use client";

import { c as _c } from "react/compiler-runtime";
import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon } from "lucide-react";
import { useRef } from "react";
import { useTheme } from "next-themes";
import { Toaster as Sonner, type ToasterProps } from "sonner";
import { useAboveDialogs } from "@/features/home-v2/topLayer";
import "@/features/home-v2/overlays.css";

/**
 * The app's toasts (`toast()` from sonner, ~50 call sites) in the v2 face:
 * unstyled sonner dressed in the v2 toast grammar — the glass card, a tone
 * key-line at the reading start (green success, red error, amber warning,
 * blue info), the display face for the title — under the header rather than
 * over it. Rendered beside the shell, so it carries its own `.v2` root.
 * The call sites, the timing and the stacking are sonner's, unchanged.
 * Its root sits in the top layer, above any open dialog (`useAboveDialogs`).
 */
const Toaster = t0 => {
  const $ = _c(9);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const {
    theme: t1
  } = useTheme();
  const theme = t1 === undefined ? "system" : t1;
  const root = useRef(null);
  useAboveDialogs(root);
  const t2 = theme as ToasterProps["theme"];
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      top: 96
    };
    t4 = {
      top: 64,
      left: 12,
      right: 12
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      unstyled: true,
      classNames: {
        toast: "v2-sonner-toast",
        title: "v2-sonner-title",
        description: "v2-sonner-desc",
        icon: "v2-sonner-icon",
        content: "v2-sonner-content",
        actionButton: "v2-btn v2-btn--sm v2-sonner-action",
        cancelButton: "v2-btn v2-btn--ghost v2-btn--sm v2-sonner-action",
        closeButton: "v2-sonner-close",
        success: "v2-sonner--success",
        error: "v2-sonner--error",
        warning: "v2-sonner--warning",
        info: "v2-sonner--info",
        loading: "v2-sonner--loading"
      }
    };
    t6 = {
      success: <CircleCheckIcon />,
      info: <InfoIcon />,
      warning: <TriangleAlertIcon />,
      error: <OctagonXIcon />,
      loading: <Loader2Icon className="v2-sonner-spin" />
    };
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] !== props || $[7] !== t2) {
    t7 = <div className="v2 v2-pop-portal v2-toast-layer" ref={root}><Sonner theme={t2} className="v2-sonner" offset={t3} mobileOffset={t4} toastOptions={t5} icons={t6} {...props} /></div>;
    $[6] = props;
    $[7] = t2;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
};
export { Toaster };